<template>
  <div class="settings-tab">
    <h4>{{ $t("works.parcel_management.choose_operation") }}</h4>
    <div class="operation-cards">
      <div
        v-for="option in operationOptions"
        :key="option.value"
        class="operation-card"
        :class="{ selected: localData.operation_type === option.value }"
        @click="selectOperationType(option.value)"
      >
        <img :src="option.icon" class="operation-card-icon" alt="" />
        <div class="operation-card-title">{{ option.title }}</div>
        <div class="operation-card-description">{{ option.description }}</div>
        <div class="operation-card-radio">
          <span class="radio-dot" v-if="localData.operation_type === option.value"></span>
        </div>
      </div>
    </div>

    <h4>{{ $t("works.parcel_management.select_parcels") }}</h4>
    <div class="parcel-select-row">
      <div class="parcel-select-box">
        <label>{{ $t("works.parcel_management.source_parcels") }}</label>
        <div class="parcel-select-value" @click="openSourceSelector">
          <span v-if="localData.source_parcels.length === 0">{{ $t("works.parcel_management.select_one_or_more") }}</span>
          <span v-else>{{ localData.source_parcels.map((p) => p.name).join(", ") }}</span>
        </div>
        <div class="multiselect-info" :class="{ 'multiselect-info-hidden': !sourceMultiselect }">
          <img src="@/assets/images/icons/alert-circle.svg" alt="" />
          <span>{{ $t("works.parcel_management.info_multiselect_source") }}</span>
        </div>
      </div>
      <div class="parcel-select-arrow">&rarr;</div>
      <div class="parcel-select-box">
        <label>{{ $t("works.parcel_management.target_parcels") }}</label>
        <div class="parcel-select-value" @click="openTargetSelector">
          <span v-if="localData.target_parcels.length === 0">{{ $t("works.parcel_management.select_target_parcel") }}</span>
          <span v-else>{{ localData.target_parcels.map((p) => p.name).join(", ") }}</span>
        </div>
        <div class="multiselect-info" :class="{ 'multiselect-info-hidden': !targetMultiselect }">
          <img src="@/assets/images/icons/alert-circle.svg" alt="" />
          <span>{{ $t("works.parcel_management.info_multiselect_target") }}</span>
        </div>
      </div>
    </div>

    <h4>{{ $t("works.parcel_management.data_to_move") }}</h4>
    <div class="data-type-row">
      <label class="data-type-checkbox">
        <input type="checkbox" v-model="localData.data_types.WORKS" @change="onDataTypeToggle('WORKS')" />
        {{ $t("works.parcel_management.data_type_works") }}
      </label>
      <label class="data-type-checkbox">
        <input type="checkbox" v-model="localData.data_types.CONSUM" @change="onDataTypeToggle('CONSUM')" />
        {{ $t("works.parcel_management.data_type_consum") }}
      </label>
      <label class="data-type-checkbox">
        <input type="checkbox" v-model="localData.data_types.PRODUCTION" @change="onDataTypeToggle('PRODUCTION')" />
        {{ $t("works.parcel_management.data_type_production") }}
      </label>
    </div>

    <ParcelSelector
      v-if="showSourceSelector"
      ref="sourceParcelSelector"
      :visible="showSourceSelector"
      :multiselect="sourceMultiselect"
      :selectedParcelUuids="localData.source_parcels.map((p) => p.uuid)"
      @close="showSourceSelector = false"
      @continue="onSourceParcelsSelected"
    />
    <ParcelSelector
      v-if="showTargetSelector"
      ref="targetParcelSelector"
      :visible="showTargetSelector"
      :multiselect="targetMultiselect"
      :selectedParcelUuids="localData.target_parcels.map((p) => p.uuid)"
      @close="showTargetSelector = false"
      @continue="onTargetParcelsSelected"
    />
  </div>
</template>

<script>
import ParcelSelector from "@/components/ParcelSelector.vue";
import { confirmDialog, errorMessage } from "@/helpers/common";

export default {
  name: "ParcelManagementSettings",
  components: {
    ParcelSelector,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localData: { ...this.formData },
      showSourceSelector: false,
      showTargetSelector: false,
    };
  },
  computed: {
    operationOptions() {
      return [
        {
          value: "MERGE",
          title: this.$t("works.parcel_management.operation_merge"),
          description: this.$t("works.parcel_management.operation_merge_description"),
          icon: require("@/assets/images/icons/parcel_merge.svg"),
        },
        {
          value: "SPLIT",
          title: this.$t("works.parcel_management.operation_split"),
          description: this.$t("works.parcel_management.operation_split_description"),
          icon: require("@/assets/images/icons/parcel_split.svg"),
        },
        {
          value: "TRANSFER",
          title: this.$t("works.parcel_management.operation_transfer"),
          description: this.$t("works.parcel_management.operation_transfer_description"),
          icon: require("@/assets/images/icons/parcel_transfer.svg"),
        },
      ];
    },
    sourceMultiselect() {
      return this.localData.operation_type === "MERGE";
    },
    targetMultiselect() {
      return this.localData.operation_type === "SPLIT";
    },
  },
  methods: {
    emitUpdate() {
      this.$emit("update-form-data", { ...this.localData });
    },
    selectOperationType(value) {
      if (this.localData.operation_type === value) {
        return;
      }
      this.localData.operation_type = value;
      this.localData.source_parcels = [];
      this.localData.target_parcels = [];
      this.localData.works_rows = [];
      this.localData.consum_rows = [];
      this.localData.production_rows = [];
      this.emitUpdate();
    },
    openSourceSelector() {
      if (!this.localData.operation_type) {
        errorMessage(this, this.$t("works.parcel_management.error_select_operation_first"));
        return;
      }
      this.showSourceSelector = true;
    },
    openTargetSelector() {
      if (!this.localData.operation_type) {
        errorMessage(this, this.$t("works.parcel_management.error_select_operation_first"));
        return;
      }
      this.showTargetSelector = true;
    },
    onSourceParcelsSelected(selectedRows) {
      const rows = selectedRows || [];
      const targetUuids = new Set((this.localData.target_parcels || []).map((p) => p.uuid).filter(Boolean));
      const hasDuplicate = rows.some((p) => targetUuids.has(p.uuid));
      if (hasDuplicate) {
        errorMessage(this, this.$t("works.parcel_management.error_same_parcel_source_target"));
        return;
      }
      this.localData.source_parcels = rows;
      this.showSourceSelector = false;
      this.emitUpdate();
    },
    onTargetParcelsSelected(selectedRows) {
      const rows = selectedRows || [];
      const sourceUuids = new Set((this.localData.source_parcels || []).map((p) => p.uuid).filter(Boolean));
      const hasDuplicate = rows.some((p) => sourceUuids.has(p.uuid));
      if (hasDuplicate) {
        errorMessage(this, this.$t("works.parcel_management.error_same_parcel_source_target"));
        return;
      }
      this.localData.target_parcels = rows;
      this.showTargetSelector = false;
      this.emitUpdate();
    },
    async onDataTypeToggle(dataType) {
      if (this.localData.data_types[dataType]) {
        this.emitUpdate();
        return; // was turned on, nothing to confirm
      }

      try {
        await confirmDialog(
          this.$t("works.parcel_management.confirm_skip_data_type_message"),
          this.$t("works.parcel_management.confirm_skip_data_type_title"),
          {
            okText: this.$t("ok"),
            cancelText: this.$t("common.cancel")
          }
        );
      } catch (e) {
        // user cancelled -> revert
        this.localData.data_types[dataType] = true;
      }

      this.emitUpdate();
    },
    validate() {
      if (!this.localData.operation_type) {
        return { error: true, message: this.$t("works.parcel_management.error_select_operation") };
      }

      if (!this.localData.source_parcels.length) {
        return { error: true, message: this.$t("works.parcel_management.error_select_source_parcels") };
      }

      if (!this.localData.target_parcels.length) {
        return { error: true, message: this.$t("works.parcel_management.error_select_target_parcels") };
      }

      const sourceUuids = new Set((this.localData.source_parcels || []).map((p) => p.uuid).filter(Boolean));
      const hasDuplicate = (this.localData.target_parcels || []).some((p) => sourceUuids.has(p.uuid));
      if (hasDuplicate) {
        return { error: true, message: this.$t("works.parcel_management.error_same_parcel_source_target") };
      }

      if (!Object.values(this.localData.data_types).some((v) => v)) {
        return { error: true, message: this.$t("works.parcel_management.error_select_data_type") };
      }

      return { error: false };
    },
  },
};
</script>

<style scoped>
.settings-tab h4 {
  margin-top: 24px;
  margin-bottom: 12px;
}

.operation-cards {
  display: flex;
  gap: 16px;
}

.operation-card {
  flex: 1;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  position: relative;
}

.operation-card.selected {
  border-color: #2e7d32;
  background-color: #f1f8f2;
}

.operation-card-title {
  font-weight: bold;
  margin-bottom: 6px;
}

.operation-card-icon {
  width: 32px;
  height: 32px;
  margin-bottom: 10px;
}

.operation-card-description {
  color: #666;
  font-size: 13px;
}

.operation-card-radio {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #2e7d32;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radio-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #2e7d32;
}

.parcel-select-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.parcel-select-box {
  flex: 1;
}

.parcel-select-box label {
  display: block;
  font-weight: bold;
  margin-bottom: 6px;
}

.parcel-select-value {
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  min-height: 20px;
  background-color: #fafafa;
}

.parcel-select-arrow {
  font-size: 20px;
  color: #666;
}

.multiselect-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 10px;
  background-color: #e8f2fc;
  border-radius: 6px;
  color: #0b5ba7;
  font-size: 12px;
}

.multiselect-info-hidden {
  visibility: hidden;
}

.multiselect-info img {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.data-type-row {
  display: flex;
  gap: 24px;
}

.data-type-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
</style>
